import {collection, documentId, getDocs, onSnapshot, query, where} from 'firebase/firestore';
import {firestore, getCollectionPath} from '../config/Firebase';
import {productConverter} from '../models/Product';
import {categoryConverter} from '../models/Category';
import Debug from './Debug';
import Text from './Text';

// Search reads the client's `search_index` (name, SKU, brand, categories, order and variants of every item, kept by
// kiosk-functions' updateSearchIndex) and its categories, kept in sync while the kiosk runs. Only the items a page
// shows are read in full from `items`.
const indexes = {};
const maxIdsPerQuery = 30;

const listen = (client, collectionName, converter, onChange) => {
    return new Promise((resolve, reject) => {
        let _collection = collection(firestore, getCollectionPath(client, collectionName));
        if (converter) _collection = _collection.withConverter(converter);
        onSnapshot(_collection, (_snapshot) => {
            _snapshot.docChanges().forEach(onChange);
            resolve();
        }, (error) => {
            Debug.printToLog('error', error);
            reject(error);
        });
    });
};

const load = (client) => {
    if (!indexes[client]) {
        const shards = new Map();
        const categories = new Map();
        const ready = Promise.all([
            listen(client, 'search_index', undefined, (change) => {
                if (change.type === 'removed') shards.delete(change.doc.id);
                else shards.set(change.doc.id, change.doc.data().items || {});
            }),
            listen(client, 'categories', categoryConverter, (change) => {
                if (change.type === 'removed') categories.delete(change.doc.id);
                else categories.set(change.doc.id, change.doc.data());
            })
        ]).then(() => ({shards: shards, categories: categories}));
        ready.catch(() => {
            delete indexes[client];
        });
        indexes[client] = ready;
    }
    return indexes[client];
};

const tokenize = (searchQuery) => {
    return searchQuery ? Text.normalize(searchQuery).trim().split(/\s+/).filter((token) => token.length > 0) : [];
};

const matchesAll = (text, tokens) => {
    return tokens.every((token) => text.includes(token));
};

const inCatalog = (categoryIds, catalog) => {
    const catalogCategories = catalog ? catalog.categories : [];
    return (categoryIds || []).some((categoryId) => catalogCategories.includes(categoryId));
};

const entryText = (entry, categories) => {
    const categoryNames = (entry.c || []).map((categoryId) => {
        const category = categories.get(categoryId);
        return category ? category.name : undefined;
    });
    return Text.normalize([entry.n, entry.s, entry.b, ...categoryNames].filter(Boolean).join(' '));
};

const sortByOrder = (matches) => {
    return matches.sort((m1, m2) => {
        const order1 = m1.order ?? Number.MAX_SAFE_INTEGER;
        const order2 = m2.order ?? Number.MAX_SAFE_INTEGER;
        if (order1 !== order2) return order1 - order2;
        return (m1.name || '').toLowerCase().localeCompare((m2.name || '').toLowerCase());
    });
};

// Builds the variant as a standalone product, so a search by a variant's name or SKU shows that variant.
const toVariantProduct = (product, variant) => {
    const variantProduct = product.clone();
    const pricesSource = variant.hasAnyPrice() ? variant : product;
    variantProduct.parent = product.id;
    variantProduct.id = variant.id;
    variantProduct.sku = variant.sku || product.sku;
    variantProduct.name = variant.name || product.name;
    variantProduct.multimedia = variant.multimedia && variant.multimedia.mainImage && variant.multimedia.mainImage.m ? variant.multimedia : product.multimedia;
    variantProduct.normalPrice = pricesSource.normalPrice;
    variantProduct.offerPrice = pricesSource.offerPrice;
    variantProduct.exclusivePrice = pricesSource.exclusivePrice;
    variantProduct.onRequest = false;
    variantProduct.variants = [];
    variantProduct.variantsMatches = [];
    return variantProduct;
};

const getItems = (client, productIds) => {
    const chunks = [];
    for (let i = 0; i < productIds.length; i += maxIdsPerQuery) chunks.push(productIds.slice(i, i + maxIdsPerQuery));
    const _collection = collection(firestore, getCollectionPath(client, 'items'));
    return Promise.all(chunks.map((chunk) => getDocs(query(_collection, where(documentId(), 'in', chunk)).withConverter(productConverter))))
        .then((snapshots) => {
            const products = new Map();
            snapshots.forEach((_snapshot) => _snapshot.forEach((_document) => products.set(_document.id, _document.data())));
            return products;
        });
};

export default class CatalogSearch {
    static minQueryLength = 3;
    static maxResults = 60;

    // Matches from the index, without reading the items: [{productId, variantId, name, order}].
    // An item matches when its name, SKU, brand or category names contain every word of the query, or one of its
    // variants does. Matches whose name has every word come first; each group is sorted by `order`.
    static match = (client, catalog, searchQuery, maxResults) => {
        const tokens = tokenize(searchQuery);
        return load(client).then(({shards, categories}) => {
            if (tokens.length === 0) return [];
            let byName = [];
            let byOther = [];
            shards.forEach((entries) => {
                Object.keys(entries).forEach((productId) => {
                    const entry = entries[productId];
                    if (!entry || !inCatalog(entry.c, catalog)) return;
                    const text = entryText(entry, categories);
                    let result;
                    if (matchesAll(text, tokens)) result = {productId: productId, variantId: undefined, name: entry.n, order: entry.o};
                    else {
                        const variant = (entry.v || []).find((variant) => matchesAll(`${text} ${Text.normalize([variant.n, variant.s].filter(Boolean).join(' '))}`, tokens));
                        if (variant) result = {productId: productId, variantId: variant.i, name: variant.n || entry.n, order: entry.o};
                    }
                    if (!result) return;
                    if (matchesAll(Text.normalize(result.name || ''), tokens)) byName.push(result);
                    else byOther.push(result);
                });
            });
            const results = sortByOrder(byName).concat(sortByOrder(byOther));
            return maxResults ? results.slice(0, maxResults) : results;
        });
    };

    // The full products of the matches, in the same order. A variant match is returned as that variant.
    static search = (client, catalog, searchQuery, maxResults) => {
        return CatalogSearch.match(client, catalog, searchQuery, maxResults || CatalogSearch.maxResults)
            .then((matches) => {
                const productIds = [...new Set(matches.map((match) => match.productId))];
                return getItems(client, productIds).then((products) => {
                    let results = [];
                    matches.forEach((match) => {
                        const product = products.get(match.productId);
                        if (!product) return;
                        const variant = match.variantId ? (product.variants || []).find((variant) => variant.id === match.variantId) : undefined;
                        results.push(variant ? toVariantProduct(product, variant) : product);
                    });
                    return results;
                });
            });
    };

    // The upsell and cross-sell products configured on the product, in that order, without repeats.
    static getRecommendedItems = (client, catalog, product) => {
        const references = (product.upsell || []).concat(product.crossSell || []);
        const productIds = [...new Set(references.map((reference) => reference.productId))].filter((productId) => productId !== product.id);
        if (productIds.length === 0) return Promise.resolve([]);
        return getItems(client, productIds).then((products) => {
            return productIds
                .map((productId) => products.get(productId))
                .filter((match) => match && inCatalog(match.categories, catalog));
        });
    };
}
